/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */

/* eslint-disable @typescript-eslint/no-unsafe-argument */

// TODO: Fix ESLint errors and improve type safety

import { apiClient } from '@/lib/api-client';
import type { LatLng } from '@/types/global';
import type {
  LocationInformation,
  Operator,
} from '../types/location-info.types';

/**
 * Map currency identifiers to symbols
 */
function getCurrencySymbol(identifier?: string): string {
  const symbols: Record<string, string> = {
    usd: '$',
    eur: '€',
    gbp: '£',
    dkk: 'kr',
    sek: 'kr',
    nok: 'kr',
    chf: 'CHF',
    cad: 'CA$',
    aud: 'A$',
    jpy: '¥',
    cny: '¥',
  };

  return (
    symbols[identifier?.toLowerCase() || ''] || identifier?.toUpperCase() || '€'
  );
}

/**
 * Fallback data when API is not available or returns errors
 * No currency info - will show generic "price per kWh" without currency
 */
const FALLBACK_DATA: LocationInformation = {
  currency: undefined,
  operators: [],
  countryCode: undefined,
  countryName: undefined,
};

/**
 * Get location information from cached operators (no API call)
 * Filters operators by country code from the map center
 *
 * @param countryCode - ISO 2-letter country code (e.g., "dk", "us", "gb")
 * @param cachedOperators - All operators from locale store cache
 * @returns LocationInformation with filtered operators for the country
 */
function getLocationInfoFromCache(
  countryCode: string | undefined,
  cachedOperators: Operator[]
): LocationInformation {
  // If no operators cached, return empty fallback
  if (cachedOperators.length === 0) {
    return FALLBACK_DATA;
  }

  // If no country code (geocoding failed), return empty operators list
  // Only show operators when we can determine the country
  if (!countryCode) {
    return FALLBACK_DATA;
  }

  // Normalize country code to lowercase for comparison
  const normalizedCode = countryCode.toLowerCase();

  // Filter operators that match the country
  const matchingOperators = cachedOperators.filter((op) => {
    const opCountryCode = op.country?.code_2?.toLowerCase();
    return opCountryCode === normalizedCode;
  });

  // Get currency from the first matching operator
  let currency: typeof FALLBACK_DATA.currency | undefined = undefined;
  let countryName: string | undefined;

  for (const operator of matchingOperators) {
    if (operator.country?.currency) {
      const curr = operator.country.currency;
      currency = {
        id: curr.id,
        identifier: curr.identifier?.toUpperCase() || 'EUR',
        name: curr.name,
        symbol: getCurrencySymbol(curr.identifier),
      };

      countryName = operator.country.name || operator.country.country_name;
      break;
    }
  }

  return {
    currency,
    operators: matchingOperators,
    countryCode: normalizedCode.toUpperCase(),
    countryName,
  };
}

/**
 * Location information API service
 */
export const locationInfoApi = {
  /**
   * Get location information from cached operators (no API call required)
   * Uses cached operators from locale store filtered by country
   *
   * @param countryCode - ISO 2-letter country code
   * @param cachedOperators - All operators from locale store
   */
  getLocationInfoFromCache,
  /**
   * Get all operators from map_suggestions endpoint
   * This is called on app start to cache all available operators
   *
   * @returns Promise resolving to array of operators with theme data
   */
  async getAllOperators(): Promise<Operator[]> {
    try {
      const response = await apiClient.get<any>(
        '/api/v1/map_suggestions?include=default_theme'
      );

      // Handle response format: {"data": [...operators]}
      let operators: any[] = [];

      if (response?.data && Array.isArray(response.data)) {
        // Response format: {"data": [operators...]}
        operators = response.data;
      } else if (Array.isArray(response)) {
        // Response format: [operators...]
        operators = response;
      }

      // Map operators list with team_ids, operator_ids, theme data, and country
      return operators.map((op: any) => ({
        id: op.id,
        name: op.name,
        logo: op.logo || op.logo_url,
        teamIds: op.team_ids || [],
        operatorIds: op.operator_ids || [],
        defaultTheme: op.default_theme,
        country: op.country,
      }));
    } catch (error: any) {
      console.error('Failed to fetch all operators:', error.message);
      return [];
    }
  },

  /**
   * Get location information (currency, operators, country) for a specific lat/lng
   * Uses Location header with format "lat,lng"
   *
   * NOTE: This endpoint requires authentication and may return 422 "Operator not defined"
   * for guest users. In that case, we return fallback data.
   */
  async getLocationInformation(location: LatLng): Promise<LocationInformation> {
    try {
      const response = await apiClient.getWithLocationHeader<any>(
        '/api/v1/countries/map_suggestions_by_lat_lng',
        location
      );

      // Handle response format: {"data": [...operators]}
      // Each operator has a nested country.currency object
      let operators: any[] = [];
      let currency: typeof FALLBACK_DATA.currency | undefined = undefined;
      let countryCode: string | undefined;
      let countryName: string | undefined;

      if (response?.data && Array.isArray(response.data)) {
        // Response format: {"data": [operators...]}
        operators = response.data;
      } else if (Array.isArray(response)) {
        // Response format: [operators...]
        operators = response;
      }

      // Loop through operators to find the first one with country.currency data
      // All operators in the same region should have the same currency
      for (const operator of operators) {
        if (operator.country?.currency) {
          const curr = operator.country.currency;
          currency = {
            id: curr.id,
            identifier: curr.identifier?.toUpperCase() || 'EUR',
            name: curr.name,
            symbol: getCurrencySymbol(curr.identifier),
          };

          // Also extract country info from the same operator
          const country = operator.country;
          countryCode = country.code_2 || country.code2 || country.country_code;
          countryName = country.name || country.country_name;

          // Found currency, no need to continue
          break;
        }
      }

      return {
        currency,
        // Map operators list with team_ids and operator_ids
        operators: operators.map((op: any) => ({
          id: op.id,
          name: op.name,
          logo: op.logo || op.logo_url,
          teamIds: op.team_ids || [],
          operatorIds: op.operator_ids || [],
        })),
        countryCode,
        countryName,
      };
    } catch (error: any) {
      // Handle 422 "Operator not defined" or other errors
      // This is expected for guest users - return fallback data
      console.warn(
        'Location info API failed, using fallback data:',
        error.message
      );
      return FALLBACK_DATA;
    }
  },
};
